import { useCallback, useMemo } from 'react';
import useAgencyFanOutQueries from './useAgencyFanOutQueries';
import {
  agencyRecommendationsKeys,
  extractArray,
  hasNotAppointedPoliciesEndpoint,
  hasStateLeaderEndpoint,
  mergePartialMeta,
  notAppointedPoliciesRequest,
  pickArray,
  pickValue,
  readPartialMeta,
  readStateLeaders,
  stateLeaderMissingAppointmentsRequest
} from './api/agencyRecommendations';

// module level so the fan-out entries stay referentially stable across renders
function policiesEntry({ agencyId }) {
  return {
    queryKey: agencyRecommendationsKeys.notAppointedPolicies(agencyId),
    request: notAppointedPoliciesRequest(agencyId)
  };
}

function stateLeadersEntry({ agencyId }) {
  return {
    queryKey: agencyRecommendationsKeys.stateLeaders(agencyId),
    request: stateLeaderMissingAppointmentsRequest(agencyId)
  };
}

function toPolicyCount(row) {
  const raw = pickValue(row, [
    'policyCount',
    'policy_count',
    'totalPolicies',
    'total_policies'
  ]);
  return Number.isFinite(Number(raw)) ? Number(raw) : 0;
}

// one card per leader × carrier they are not appointed with. a leader with no
// missing carriers produces nothing.
function coverageGapsForLeader(leader, { agencyId, agencyName }) {
  const carriers = pickArray(leader, [
    'carriersNotAppointed',
    'carriers_not_appointed',
    'missingCarriers',
    'missing_carriers',
    'carriers'
  ]);
  if (carriers.length === 0) return [];

  const agentId = pickValue(leader, ['agentId', 'agent_id']);
  const agentName = pickValue(leader, ['agentName', 'agent_name']);
  const agentEmail = pickValue(leader, ['agentEmail', 'agent_email']);
  const agentNpn = pickValue(leader, ['agentNpn', 'agent_npn', 'npn', 'NPN']);
  const state = pickValue(leader, ['state', 'State']);
  const policyCount = toPolicyCount(leader);

  return carriers.filter(Boolean).map(carrier => ({
    agentId,
    agentName,
    agentEmail,
    agentNpn,
    state,
    policyCount,
    carrierId: pickValue(carrier, ['id', 'carrierId', 'carrier_id']),
    carrierName: pickValue(carrier, ['name', 'carrierName', 'carrier_name']),
    agencyId,
    agencyName
  }));
}

/**
 * loads the two per-agency recommendation lists behind the bottom cards: policies
 * sold without an appointment ("Commission at risk") and the top state leaders'
 * missing carriers ("Coverage gap").
 * @param agencies - list of { agencyId, agencyName } currently applied
 */
export default function useAgencyRecommendations(agencies) {
  const policiesFan = useAgencyFanOutQueries(agencies, policiesEntry, {
    enabled: hasNotAppointedPoliciesEndpoint()
  });
  const stateLeadersFan = useAgencyFanOutQueries(agencies, stateLeadersEntry, {
    enabled: hasStateLeaderEndpoint()
  });

  const policies = useMemo(
    () =>
      policiesFan.results.flatMap(({ agencyId, agencyName, data }) =>
        extractArray(data).map(policy => ({ ...policy, agencyId, agencyName }))
      ),
    [policiesFan.results]
  );

  // double flatten: leaders (up to 3 per agency) × carriersNotAppointed
  const coverageGaps = useMemo(
    () =>
      stateLeadersFan.results.flatMap(({ agencyId, agencyName, data }) =>
        readStateLeaders(data).flatMap(leader =>
          coverageGapsForLeader(leader, { agencyId, agencyName })
        )
      ),
    [stateLeadersFan.results]
  );

  const coverageGapsPartialMeta = useMemo(
    () =>
      mergePartialMeta(
        stateLeadersFan.results.map(({ data }) => readPartialMeta(data))
      ),
    [stateLeadersFan.results]
  );

  const failedAgencies = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const a of [
      ...policiesFan.failedAgencies,
      ...stateLeadersFan.failedAgencies
    ]) {
      if (seen.has(a.agencyId)) continue;
      seen.add(a.agencyId);
      combined.push(a);
    }
    return combined;
  }, [policiesFan.failedAgencies, stateLeadersFan.failedAgencies]);

  const retryFailed = useCallback(
    () =>
      Promise.all([policiesFan.retryFailed(), stateLeadersFan.retryFailed()]),
    [policiesFan.retryFailed, stateLeadersFan.retryFailed]
  );

  return {
    loadingPolicies: policiesFan.isLoading,
    policies,
    loadingStateLeaders: stateLeadersFan.isLoading,
    coverageGaps,
    coverageGapsPartialMeta,
    failedAgencies,
    retryFailed
  };
}
